import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Globe, Languages, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useSeo } from "@/hooks/useSeo";


/**
 * /settings/language — Grab-style language + auto-translate settings.
 * · Tap "App language" to open the picker
 * · Toggle "Auto-translations" to translate menu / reviews / chat inline
 */
export default function LanguageSettings() {
  const { lang, setLang, languages, t } = useI18n();
  const LANGS = Object.entries(languages).map(([code, l]) => ({ code, label: l.name, native: l.native, status: l.status }));
  const [autoTr, setAutoTr] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  useSeo({ title: "Language — Find2Near" });

  useEffect(() => {
    try { const s = localStorage.getItem("f2n_auto_translate"); if (s !== null) setAutoTr(s === "1"); } catch { /* ignore */ }
  }, []);
  const toggleAuto = () => {
    const next = !autoTr; setAutoTr(next);
    try { localStorage.setItem("f2n_auto_translate", next ? "1" : "0"); } catch { /* ignore */ }
  };
  const current = LANGS.find(l => l.code === lang) || LANGS[0];

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white" data-testid="lang-settings">
      <div className="flex items-center gap-3 px-4 pt-5 pb-4">
        <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="lang-back">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="heading font-black text-2xl text-stone-900">{t("locale.language")}</h1>
      </div>

      {/* App language row */}
      <button
        onClick={() => setPickerOpen(true)}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-stone-50"
        data-testid="lang-app-row"
      >
        <div className="w-11 h-11 rounded-full bg-emerald-100 grid place-items-center">
          <Globe size={20} className="text-emerald-700" />
        </div>
        <div className="flex-1 text-left">
          <div className="font-bold text-stone-900">App language</div>
          <div className="text-sm text-stone-500" data-testid="lang-app-value">{current.native}</div>
        </div>
        <span className="text-stone-400 text-xl">›</span>
      </button>

      {/* Auto-translations toggle row */}
      <div className="w-full flex items-start gap-3 px-4 py-4 border-t border-stone-100" data-testid="lang-auto-row">
        <div className="w-11 h-11 rounded-full bg-emerald-100 grid place-items-center">
          <Languages size={20} className="text-emerald-700" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-stone-900">Auto-translations</div>
          <div className="text-sm text-stone-500 mt-1">
            Always translate content like menu items, reviews, chat messages from other languages to your preferred language.
          </div>
        </div>
        <button
          onClick={toggleAuto}
          className={`relative w-12 h-7 rounded-full transition-colors ${autoTr ? "bg-emerald-500" : "bg-stone-300"}`}
          aria-pressed={autoTr}
          data-testid="lang-auto-toggle"
        >
          <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${autoTr ? "left-5" : "left-0.5"}`} />
        </button>
      </div>

      {/* Picker sheet */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black/60 z-[9600] grid place-items-end md:place-items-center p-0 md:p-6" onClick={() => setPickerOpen(false)} data-testid="lang-picker">
          <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="heading font-bold text-lg mb-3">Choose app language</div>
            <div className="space-y-1">
              {LANGS.map(l => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setPickerOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-stone-50 ${l.code === lang ? "bg-emerald-50" : ""}`}
                  data-testid={`lang-opt-${l.code}`}
                >
                  <div className="flex-1 text-left">
                    <div className="font-bold text-stone-900">{l.native}</div>
                    <div className="text-xs text-stone-500">{l.label} · <span data-testid={`lang-opt-status-${l.code}`}>{l.status === "supported" ? t("locale.status_supported") : t("locale.status_preview")}</span></div>
                  </div>
                  {l.code === lang && <Check size={16} className="text-emerald-600" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
